import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import ShareEventButton from "@/components/ShareEventButton";
import { getEventThumb } from "@/lib/eventImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarIcon, MapPin, ArrowRight, Search, Filter, Grid, List, CalendarDays } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Blue vs Blue event images
const EVENT_IMAGES = [
  "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/mrhmn1tv_462677696162527021.jpeg",
  "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/l3pyanjc_6950815501382336868.jpeg",
  "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/t46e1ukx_3616082471350714836.jpeg"
];

const categories = [
  { value: "all", label: "All Categories" },
  { value: "awareness", label: "Awareness Events" },
  { value: "fundraiser", label: "Fundraisers" },
  { value: "community", label: "Community Events" },
  { value: "screening", label: "Health Screenings" },
  { value: "general", label: "General" }
];

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [showPast, setShowPast] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchEvents();
  }, [showPast]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events?upcoming=${!showPast}`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    const q = searchQuery.toLowerCase();
    const title = (event.title || "").toLowerCase();
    const desc = (event.description || "").toLowerCase();
    const matchesSearch = title.includes(q) || desc.includes(q);
    const matchesCategory = category === "all" || event.category === category;
    return matchesSearch && matchesCategory;
  });

  const safeFormat = (dateStr, fmt) => {
    if (!dateStr) return "Date TBA";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "Date TBA";
      return format(d, fmt);
    } catch { return "Date TBA"; }
  };

  // Calendar helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Add padding days for the calendar grid
  const startDayOfWeek = monthStart.getDay();
  const paddingDays = Array(startDayOfWeek).fill(null);

  const getEventsForDay = (day) => {
    return filteredEvents.filter(event => {
      if (!event.date) return false;
      try {
        const eventDate = parseISO(event.date);
        if (isNaN(eventDate.getTime())) return false;
        return isSameDay(eventDate, day);
      } catch { return false; }
    });
  };

  const getEventImage = (index) => EVENT_IMAGES[index % EVENT_IMAGES.length];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="gradient-hero py-16" data-testid="events-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Events</span>
            <h1 className="text-heading-1 text-white mt-4 mb-4">Upcoming Events</h1>
            <p className="text-white/80 text-lg">
              Join us at our events to support prostate cancer awareness and connect with our community.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 border-b border-slate-200" data-testid="events-filters">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="events-search"
                />
              </div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-full sm:w-48" data-testid="events-category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant={!showPast ? "default" : "outline"}
                onClick={() => setShowPast(false)}
                size="sm"
                data-testid="upcoming-events-btn"
              >
                Upcoming
              </Button>
              <Button
                variant={showPast ? "default" : "outline"}
                onClick={() => setShowPast(true)}
                size="sm"
                data-testid="past-events-btn"
              >
                Past Events
              </Button>
              <div className="hidden sm:flex border rounded-md">
                <Button 
                  variant={viewMode === "grid" ? "secondary" : "ghost"} 
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  data-testid="grid-view-btn"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button 
                  variant={viewMode === "list" ? "secondary" : "ghost"} 
                  size="icon"
                  onClick={() => setViewMode("list")}
                  data-testid="list-view-btn"
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button 
                  variant={viewMode === "calendar" ? "secondary" : "ghost"} 
                  size="icon"
                  onClick={() => setViewMode("calendar")}
                  data-testid="calendar-view-btn"
                >
                  <CalendarDays className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Listing */}
      <section className="section-spacing" data-testid="events-listing">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-slate-100 rounded-lg h-80 animate-pulse"></div>
              ))}
            </div>
          ) : viewMode === "calendar" ? (
            /* Calendar View */
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-testid="calendar-view">
              {/* Calendar Header */}
              <div className="flex items-center justify-between p-4 bg-deep-blue text-white">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="text-white hover:bg-white/10"
                >
                  Previous
                </Button>
                <h2 className="text-xl font-bold font-heading">
                  {format(currentMonth, "MMMM yyyy")}
                </h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="text-white hover:bg-white/10"
                >
                  Next
                </Button>
              </div>
              
              {/* Day Headers */}
              <div className="grid grid-cols-7 bg-slate-50 border-b">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                  <div key={day} className="p-3 text-center text-sm font-semibold text-slate-600">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {paddingDays.map((_, idx) => (
                  <div key={`pad-${idx}`} className="min-h-[100px] p-2 border-b border-r bg-slate-50"></div>
                ))}
                {calendarDays.map((day, idx) => {
                  const dayEvents = getEventsForDay(day);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div 
                      key={idx} 
                      className={`min-h-[100px] p-2 border-b border-r ${isToday ? 'bg-awareness-blue/5' : ''}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'bg-awareness-blue text-white w-7 h-7 rounded-full flex items-center justify-center' : 'text-slate-700'}`}>
                        {format(day, "d")}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map(event => (
                          <Link 
                            key={event.event_id} 
                            to={`/events/${event.event_id}`}
                            className="block text-xs bg-deep-blue text-white px-2 py-1 rounded truncate hover:bg-deep-blue/80"
                          >
                            {event.title}
                          </Link>
                        ))}
                        {dayEvents.length > 2 && (
                          <span className="text-xs text-slate-500">+{dayEvents.length - 2} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : filteredEvents.length > 0 ? (
            viewMode === "grid" ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredEvents.map((event, idx) => (
                  <Link 
                    key={event.event_id}
                    to={`/events/${event.event_id}`}
                    className="card-event"
                    data-testid={`event-card-${event.event_id}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={getEventThumb(event, getEventImage(idx))}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.currentTarget.src = getEventImage(idx); }}
                      />
                    </div>
                    <div className="p-6">
                      <span className="inline-block bg-awareness-blue/10 text-awareness-blue text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase">
                        {event.category}
                      </span>
                      <h3 className="text-heading-3 text-deep-blue mb-2 group-hover:text-awareness-blue transition-colors">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
                        <CalendarIcon className="w-4 h-4" />
                        {safeFormat(event.date, "MMMM d, yyyy • h:mm a")}
                      </div>
                      <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </div>
                      <div className="mt-4 flex items-center justify-between text-deep-blue font-semibold text-sm">
                        <span className="flex items-center">View Details <ArrowRight className="w-4 h-4 ml-1" /></span>
                        <ShareEventButton event={event} stopPropagation variant="icon" data-testid={`share-card-${event.event_id}`} />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredEvents.map((event, idx) => (
                  <Link 
                    key={event.event_id}
                    to={`/events/${event.event_id}`}
                    className="flex flex-col md:flex-row gap-6 bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg transition-shadow"
                    data-testid={`event-list-${event.event_id}`}
                  >
                    <div className="md:w-48 h-32 overflow-hidden rounded-lg flex-shrink-0">
                      <img 
                        src={getEventThumb(event, getEventImage(idx))}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.src = getEventImage(idx); }}
                      />
                    </div>
                    <div className="flex-1">
                      <span className="inline-block bg-awareness-blue/10 text-awareness-blue text-xs font-semibold px-3 py-1 rounded-full mb-2 uppercase">
                        {event.category}
                      </span>
                      <h3 className="text-heading-3 text-deep-blue mb-2">{event.title}</h3>
                      <div className="flex flex-wrap gap-4 text-slate-500 text-sm mb-2">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {safeFormat(event.date, "MMMM d, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm line-clamp-2">{event.description}</p>
                    </div>
                    <div className="md:self-center md:ml-4 flex-shrink-0">
                      <ShareEventButton event={event} stopPropagation variant="icon" data-testid={`share-list-${event.event_id}`} />
                    </div>
                  </Link>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-16" data-testid="no-events">
              <CalendarIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Events Found</h3>
              <p className="text-slate-500">
                {showPast 
                  ? "No past events match your criteria." 
                  : "Check back soon for upcoming events!"}
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
